<script setup>
import ActionButton from "@js/components/ActionButton.vue";
import Drawer from "@js/components/Drawer.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import InputField from "@js/components/fields/InputField.vue";
import { useRoute } from "@js/utils/route";
import { Form } from "vee-validate";
import { reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { object, string } from "yup";

const { t } = useI18n();

const { callback } = defineProps({
  callback: {
    type: Function,
    required: false,
  },
});

const { route } = useRoute();

const loading = ref(true);

const isOpen = ref(false);

const form = reactive({
  value: null,
});

const validationSchema = object({
  value: string().required(t("error.required")),
});

function setIsOpen(value) {
  if (value) {
    updateData();
  }

  isOpen.value = value;
}

function updateData() {
  loading.value = true;

  axios
    .get(
      route("front.getConfiguration", {
        key: "latest_prestashop_version",
      }),
    )
    .then((response) => {
      form.value = response.data.data.value;
    })
    .catch((error) => {
      console.log(error);
    })
    .finally(() => {
      loading.value = false;
    });
}

function saveAction() {
  let url = route("front.postConfiguration", {
    key: "latest_prestashop_version",
  });

  let postData = {
    data: {
      value: form.value,
    },
  };

  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-TOKEN": document
        .querySelector('meta[name="csrf-token"]')
        .getAttribute("content"),
    },
    body: JSON.stringify(postData),
  })
    .then((response) => response.json())
    .then(() => {
      setIsOpen(false);

      if (callback) {
        callback();
      }
    })
    .catch((error) => {
      console.error(error);
    });
}
</script>

<template>
  <slot name="button" :set-is-open="setIsOpen" />

  <Drawer :is-open="isOpen" :set-is-open="setIsOpen">
    <template #title>
      {{ t("title.updateLatestPrestashopVersion") }}
    </template>

    <template #full-content>
      <LoadingSpinner v-if="loading" />

      <Form
        v-else
        :validation-schema="validationSchema"
        @submit="saveAction"
        v-slot="{ errors }"
        class="flex h-full flex-col"
      >
        <div class="p-6">
          <InputField
            v-model="form.value"
            :label="t('label.latestVersion')"
            field-name="value"
            field-id="value"
            type="text"
            required
          />
        </div>

        <div
          class="mt-auto flex flex-wrap items-center gap-2 border-t border-t-gray-200 p-6"
        >
          <ActionButton submit :disabled="Object.keys(errors).length > 0">
            {{ t("button.save") }}
          </ActionButton>
        </div>
      </Form>
    </template>
  </Drawer>
</template>
